import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Badge, Grid } from "@mui/material";
import { sprintf } from "sprintf-js";
import { baseURL } from "../../api";
import { Global_Data } from "../../globalData/GlobalData";
import ComponentLoadingBoundary from "../../component/Loading/ComponentLoadingBoundary";
import usePendingRequests from "../../component/Loading/usePendingRequests";

const findComponentTypeByName = (nodes, componentName) => {
  if (!Array.isArray(nodes)) return null;

  for (const node of nodes) {
    if (node?.component?.sName === componentName) {
      return node?.component?.sType;
    }

    const found = findComponentTypeByName(node?.child, componentName);

    if (found) {
      return found;
    }
  }

  return null;
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  };
}

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key];
  }, obj);
};

const getAwareKeys = aware => {
  if (!aware) return [];

  const matches = String(aware).match(/{(.*?)}/g);

  if (matches?.length) {
    return matches.map(item => item.replace(/[{}]/g, ""));
  }

  return String(aware)
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);
};

const replaceAwareParams = (uri, source = {}) => {
  if (!uri) return uri;

  return uri.replace(/{([^}]+)}/g, (_, key) => {
    return source?.[key] ?? "";
  });
};

const getAwareSignature = (awareKeys, textValue, dataGridOptions) => {
  if (!awareKeys?.length) return "";

  return awareKeys
    .map(key => {
      const textAwareValue = textValue?.[key];

      const gridAwareValue =
        dataGridOptions?.dataGrid === key
          ? JSON.stringify(dataGridOptions)
          : "";

      return `${key}:${textAwareValue ?? ""}:${gridAwareValue}`;
    })
    .join("|");
};

export default function BasicTabs({ data, renderComponent }) {
  const [value, setValue] = useState(0);
  const [tabCounts, setTabCounts] = useState({});
  const { token, textValue, dataGridOptions } = Global_Data() || {};
  const { loading, trackRequest } = usePendingRequests();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const countRefreshSignature = useMemo(() => {
    const allAwareKeys =
      data?.child
        ?.flatMap(item => getAwareKeys(item?.data?.sDataAware))
        ?.filter(Boolean) || [];

    return getAwareSignature(allAwareKeys, textValue, dataGridOptions);
  }, [data, textValue, dataGridOptions]);

  useEffect(() => {
    if (!data?.child?.length) return;

    let isMounted = true;

    const fetchTabCount = async (item, index) => {
      const tabData = item?.data;

      if (!tabData?.bDisplayCount || !tabData?.sDataSource) {
        return;
      }

      const awareKeys = getAwareKeys(tabData?.sDataAware);

      const hasMissingTextAwareValue = awareKeys.some(key => {
        const awareComponentType = findComponentTypeByName(data?.child, key);
        const isDataGridAware = awareComponentType === "DATAGRID";

        if (isDataGridAware) {
          return false;
        }

        const value = textValue?.[key];

        return value === undefined || value === null || value === "";
      });

      if (hasMissingTextAwareValue) {
        setTabCounts(prev => ({
          ...prev,
          [index]: ""
        }));
        return;
      }

      try {
        const sourceUrl = replaceAwareParams(tabData.sDataSource, textValue);

        const response = await trackRequest(() =>
          fetch(`${baseURL}${sourceUrl}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        );

        const result = await response.json();
        const rawValue = getValueByPath(result, tabData.sDataField);
        const numericValue = Number(rawValue || 0);

        const formattedValue = tabData.sCountFormat
          ? sprintf(tabData.sCountFormat, numericValue)
          : numericValue;

        if (!isMounted) return;

        setTabCounts(prev => ({
          ...prev,
          [index]: formattedValue
        }));
      } catch (error) {
        console.error("Error fetching tab count:", item?.component?.sName, error);

        if (!isMounted) return;

        setTabCounts(prev => ({
          ...prev,
          [index]: ""
        }));
      }
    };

    data.child.forEach((item, index) => {
      fetchTabCount(item, index);
    });

    return () => {
      isMounted = false;
    };
  }, [data, token, textValue, dataGridOptions, countRefreshSignature]);

  return (
    <ComponentLoadingBoundary
      loading={loading}
      label={`Loading ${data?.component?.sName}`}
      minHeight={48}
      size={20}
    >
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          id={data?.component?.sName}
          {...data?.component?.sProps}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          {data?.child?.map((item, index) => {
            const displayCount = item?.data?.bDisplayCount === true;
            const countValue = tabCounts[index];

            return (
              <Tab
                key={index}
                id={item?.component?.sName}
                {...a11yProps(index)}
                label={
                  displayCount ? (
                    <Badge
                      badgeContent={countValue}
                      color="primary"
                      showZero
                      sx={{ pr: 2 }}
                    >
                      <span>{item?.component?.sLabel}</span>
                    </Badge>
                  ) : (
                    item?.component?.sLabel
                  )
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {data?.child?.map((item, index) => (
        <TabPanel
          id={item?.component?.sName}
          value={value}
          index={index}
          key={index}
        >
          <Grid container>
            {renderComponent(item?.child)}
          </Grid>
        </TabPanel>
      ))}
    </Box>
    </ComponentLoadingBoundary>
  );
}
